import { Quaternion } from 'three/webgpu'
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import type { ShipDef } from '../data/schemas'
import { cameraTuning, type CameraRigTuning } from '../features/camera/tuning'

export type CameraMode = 'chase' | 'cockpit'

/** Camera pose in float64 world space. The three.js camera itself always sits at the origin. */
export interface CameraRig {
  mode: CameraMode
  /** Camera position, float64 world space (the render origin). */
  pos: Vec3
  /** Final camera orientation (the loop copies it to the three.js camera; the camera feature adds shake on top). */
  rot: Quaternion
  /** The spring-lagged ship orientation the chase arm rides on (D-021). Equals the ship orientation in the cockpit. */
  lag: Quaternion
  /** False until the first update, and after `resetCameraRig`: the next update snaps. */
  initialized: boolean
  /** Look-ahead, aim height, roll lag and snap threshold. Shared with `cameraTuning.rig` by default so the dev panel tunes it live. */
  tuning: CameraRigTuning
}

export function createCameraRig(tuning: CameraRigTuning = cameraTuning.rig): CameraRig {
  return {
    mode: 'chase',
    pos: { x: 0, y: 0, z: 0 },
    rot: new Quaternion(),
    lag: new Quaternion(),
    initialized: false,
    tuning,
  }
}

/** Makes the next update snap to the ship (respawn, teleport). */
export function resetCameraRig(rig: CameraRig): void {
  rig.initialized = false
}

// Module scratch (D-014: the rig runs every frame and allocates nothing).
const target = new Quaternion()
const delta = new Quaternion()
const twist = new Quaternion()
const swing = new Quaternion()
const step = new Quaternion()
const offset: Vec3 = { x: 0, y: 0, z: 0 }
const ahead: Vec3 = { x: 0, y: 0, z: 0 }
const up: Vec3 = { x: 0, y: 0, z: 0 }

const EPS = 1e-9
const DEG = Math.PI / 180

/** Angle (radians) of the rotation a unit quaternion describes, taking the shorter way round. */
function rotationAngle(q: Quaternion): number {
  const w = Math.min(1, Math.abs(q.w))
  return 2 * Math.acos(w)
}

/**
 * Advances the lagged orientation toward `target` with a frame-rate independent critically damped spring.
 * The ship-local error splits (swing-twist) into pitch/yaw (swing) and roll about the forward axis (twist), so
 * roll can follow with its own, softer stiffness: the horizon rolls gently, which reads as weight and keeps
 * the view comfortable.
 */
function stepLag(lag: Quaternion, stiffness: number, rollStiffness: number, dt: number): void {
  // delta = lag⁻¹ · target (error in the lagged frame), shortest path.
  delta.copy(lag).invert().multiply(target)
  if (delta.w < 0) delta.set(-delta.x, -delta.y, -delta.z, -delta.w)

  // Twist about local Z (the forward axis), swing = delta · twist⁻¹, so delta = swing · twist.
  const tz = delta.z
  const tw = delta.w
  const tl = Math.sqrt(tz * tz + tw * tw)
  if (tl < EPS) twist.identity()
  else twist.set(0, 0, tz / tl, tw / tl)
  swing.copy(twist).invert()
  swing.premultiply(delta)

  const aSwing = 1 - Math.exp(-stiffness * dt)
  const aRoll = 1 - Math.exp(-rollStiffness * dt)
  lag.multiply(step.identity().slerp(swing, aSwing))
  lag.multiply(step.identity().slerp(twist, aRoll))
  lag.normalize()
}

/**
 * Writes `out` as the orientation whose -Z looks along (fx, fy, fz) with +Y as close to (ux, uy, uz) as possible.
 * Returns false (and leaves `out` alone) when the direction is degenerate or parallel to the up hint.
 */
function lookRotation(fx: number, fy: number, fz: number, ux: number, uy: number, uz: number, out: Quaternion): boolean {
  const fl = Math.sqrt(fx * fx + fy * fy + fz * fz)
  if (fl < EPS) return false
  // Basis columns: z = -forward, x = up × z, y = z × x.
  const zx = -fx / fl
  const zy = -fy / fl
  const zz = -fz / fl
  let xx = uy * zz - uz * zy
  let xy = uz * zx - ux * zz
  let xz = ux * zy - uy * zx
  const xl = Math.sqrt(xx * xx + xy * xy + xz * xz)
  if (xl < EPS) return false
  xx /= xl
  xy /= xl
  xz /= xl
  const yx = zy * xz - zz * xy
  const yy = zz * xx - zx * xz
  const yz = zx * xy - zy * xx
  // Rotation matrix → quaternion (Shepperd), matrix m[row][col] with columns x, y, z.
  const m00 = xx
  const m01 = yx
  const m02 = zx
  const m10 = xy
  const m11 = yy
  const m12 = zy
  const m20 = xz
  const m21 = yz
  const m22 = zz
  const tr = m00 + m11 + m22
  if (tr > 0) {
    const s = 0.5 / Math.sqrt(tr + 1)
    out.set((m21 - m12) * s, (m02 - m20) * s, (m10 - m01) * s, 0.25 / s)
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22)
    out.set(0.25 * s, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s)
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22)
    out.set((m01 + m10) / s, 0.25 * s, (m12 + m21) / s, (m02 - m20) / s)
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11)
    out.set((m02 + m20) / s, (m12 + m21) / s, 0.25 * s, (m10 - m01) / s)
  }
  out.normalize()
  return true
}

/**
 * Chase (D-021): the arm's orientation lags the ship with a spring (roll softer than pitch/yaw) and the
 * position rides that lagged orientation, so there is no positional lag and the camera distance never changes
 * with speed. The camera then aims at the point `tuning.lookAhead` metres ahead of the ship's nose (raised by
 * `lookAheadHeight` × the arm height), which leads turns (you see where you are going) while the ship visibly
 * swings in the frame.
 * Cockpit: rigid at `def.eyePoint` (EYE_PILOT + eyeOffset) with the ship's orientation.
 * Allocation-free; world positions stay float64 (only ship-relative offsets are computed).
 */
export function updateCameraRig(rig: CameraRig, shipPos: Vec3, shipRot: Quat, def: ShipDef, dt: number): void {
  target.set(shipRot.x, shipRot.y, shipRot.z, shipRot.w)

  if (rig.mode === 'cockpit') {
    rig.lag.copy(target)
    rig.rot.copy(target)
    rig.initialized = true
    const e = def.eyePoint
    rotateVec(target, e[0], e[1], e[2], offset)
    rig.pos.x = shipPos.x + offset.x
    rig.pos.y = shipPos.y + offset.y
    rig.pos.z = shipPos.z + offset.z
    return
  }

  const t = rig.tuning
  if (!rig.initialized || rotationAngle(delta.copy(rig.lag).invert().multiply(target)) > t.snapAngleDeg * DEG) {
    rig.lag.copy(target)
    rig.initialized = true
  } else if (dt > 0) {
    stepLag(rig.lag, def.chaseRotStiffness, def.chaseRotStiffness * t.rollLagScale, dt)
  }

  const o = def.chaseOffset
  rotateVec(rig.lag, o[0], o[1], o[2], offset)
  rig.pos.x = shipPos.x + offset.x
  rig.pos.y = shipPos.y + offset.y
  rig.pos.z = shipPos.z + offset.z

  // Aim from the camera (ship + offset) at the look-ahead point (ship frame: lookAheadHeight × the arm height up,
  // lookAhead metres out along the nose): direction = ahead - offset, all ship-relative.
  rotateVec(target, 0, o[1] * t.lookAheadHeight, -t.lookAhead, ahead)
  rotateVec(rig.lag, 0, 1, 0, up)
  if (!lookRotation(ahead.x - offset.x, ahead.y - offset.y, ahead.z - offset.z, up.x, up.y, up.z, rig.rot)) {
    rig.rot.copy(rig.lag)
  }
}
